import './PersonalSubscriptionInfo.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useRouter } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';

import {
  Block,
  Button,
  Header,
  InlineHeader,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { useCurrentUserId, useCurrentUserInfo, useTranslator } from '$hooks';
import { Api } from '$api';
import { Loader, ShareSheet, SubscriptionIcon, UserCell } from '$shared/components';
import { AttachLink, CurrencyEnum, SubscriptionType } from '$store/models';
import { PERSONAL_SUBSCRIPTION, TRIAL_LINKS } from '$shared/constants/pages';
import { IcEdit28 } from '$assets';
import { mainActions } from '$store/main';
import { formatCurrency } from '$utils';
import { showModal } from '$navigation/helper';
import { useCurrentUserIsTester } from '$hooks/useUserInfo';

export const PersonalSubscriptionInfo: FC = () => {
  const t = useTranslator();
  const isTester = useCurrentUserIsTester();
  const userId = useCurrentUserId();
  const router = useRouter();
  const dispatch = useDispatch();
  const [loadingState, setLoadingState] = useState('loading');
  const [data, setData] = useState<{
    isEnabled: boolean;
    price: string;
    tonPrice: number;
    subscribersCount: number;
    subscribers: number[];
    trialLinks: number;
  } | null>(null);
  const user = useCurrentUserInfo();
  const isModel = user.verified > 0;
  const currency = isModel ? CurrencyEnum.EUR : CurrencyEnum.TON;

  const loadData = useCallback(() => {
    setLoadingState('loading');
    Api.get('/balance/subscription/personal')
      .then((resp) => {
        const d: any = resp;
        dispatch(mainActions.setUsers(d.users));
        setData(d);
        setLoadingState('none');
      })
      .catch(() => {
        setLoadingState('failed');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const handleEnable = useCallback(() => {
    router.navigate(PERSONAL_SUBSCRIPTION);
  }, []);

  const handleShare = useCallback(() => {
    const link: AttachLink = {
      type: 'personal_subscription',
      object: {
        id: user.id,
        name: user.firstName + ' ' + user.lastName,
        photo: user.photo,
      },
    };

    dispatch(
      mainActions.setPopup(<ShareSheet title={t('post_share_title')} link={link} />),
    );
  }, [user]);

  function renderContent() {
    if (loadingState === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingState === 'loading' || !data) {
      return <Loader isWrapped />;
    }

    if (!data.isEnabled) {
      return (
        <Placeholder
          centered
          title={t('personal_subscription_placeholder_title')}
          icon={<SubscriptionIcon type={SubscriptionType.Author} authorId={0} />}
          actions={
            <Button size="small" onClick={handleEnable}>
              {t('personal_subscription_placeholder_btn')}
            </Button>
          }
        >
          {t('personal_subscription_placeholder_description')}
        </Placeholder>
      );
    }

    return (
      <>
        <div className="PersonalSubscriptionInfo__info_wrap">
          <List>
            <ListItem indicator={t('personal_subscription_info_activated')}>
              {t('personal_subscription_info_status')}
            </ListItem>
            <ListItem indicator={formatCurrency(+data.price, currency)}>
              {t('personal_subscription_info_price')}
            </ListItem>
            <ListItem indicator={`${data.subscribersCount}`}>
              {t('personal_subscription_info_subscribers')}
            </ListItem>
          </List>
          {isModel && (
            <List>
              <ListItem chevron routeName={TRIAL_LINKS} indicator={data.trialLinks}>
                {t('personal_subscription_trial_links')}
              </ListItem>
            </List>
          )}
          <Block>
            <div className="PersonalSubscriptionInfo__share">
              <div className="PersonalSubscriptionInfo__share__caption">
                {t('personal_subscription_info_share')}
              </div>
              <Button onClick={handleShare}>
                {t('personal_subscription_info_share_btn')}
              </Button>
            </div>
          </Block>
        </div>
        {data.subscribers.length > 0 ? (
          <>
            <InlineHeader>
              {t('personal_subscription_info_subscribers_title')}
            </InlineHeader>
            {data.subscribers.map((userId) => (
              <UserCell isSponsor={isModel} key={userId} objectId={userId} skipButton />
            ))}
          </>
        ) : (
          <Placeholder>{t('personal_subscription_info_subscribers_empty')}</Placeholder>
        )}
      </>
    );
  }

  return (
    <>
      <Header
        showBackButton
        rightIcons={
          data && +data.price > 0
            ? [
                {
                  icon: <IcEdit28 />,
                  onClick: handleEnable,
                },
              ]
            : undefined
        }
      >
        {t('personal_subscription_info_title')}
      </Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
